import React, { createContext, useContext, useReducer, useEffect, useCallback, useState } from 'react';
import axios from 'axios';
import produce, { current } from 'immer';
import { Device, PreflightTest } from '@twilio/voice-sdk';
import { UAParser } from 'ua-parser-js';
import useTwilioStatus from './useTwilioStatus/useTwilioStatus';
import useConferencingStatus from './useConferencingStatus/useConferencingStatus';
import { AudioInputTest, AudioOutputTest } from '@twilio/rtc-diagnostics';
import {
  capitaliseFirstLetter,
  createUTCTimestamp,
  edgeNameMap,
  getConnectivityStatus,
  getFormattedDate,
  round,
} from '../../utils';
import { TOKEN_API_URL } from '../../constants';
import { TestResults } from '../../types';
import { getQualityScore } from '../panes/Quality/getQualityScore/getQualityScore';
import { QualityScore } from '../panes/Quality/Quality';

export enum ActivePane {
  GetStarted,
  UserInfo,
  DeviceCheck,
  DeviceError,
  AudioTest,
  BrowserTest,
  NetworkTest,
  Connectivity,
  Quality,
  Results,
}

export type TwilioAPIStatus = 'operational' | 'major_outage' | 'partial_outage' | 'degraded_performance';

export interface TwilioStatus {
  ['Conference US1']?: TwilioAPIStatus;
  ['Client Web US1']?: TwilioAPIStatus;
  ['Voice REST API US1']?: TwilioAPIStatus;
  ['Recording US1']?: TwilioAPIStatus;
}

export type ConferencingStatus = {
  bookingApiStatus: string;
  conferenceApiStatus: string;
  phoneNumberApiStatus: string;
};

export type UserInfo = {
  email: string;
};

export interface PreflightTestReport {
  callQuality?: string;
  stats: {
    rtt?: { average?: number; max?: number };
    jitter?: { average?: number; max?: number };
    packetLoss?: { average?: number };
  };
  totals?: { packetsLostFraction?: number };
}

interface stateType {
  activePane: ActivePane;
  userInfo: UserInfo | null;
  audioGranted: boolean;
  deviceError: null | Error;
  preflightTest: {
    progress: string | null;
    error: null | Error;
    report: null | PreflightTestReport;
    tokenError: null | Error;
  };
  twilioStatus: TwilioStatus | null;
  twilioStatusError: null | Error;
  conferencingStatus: ConferencingStatus | null;
  conferencingStatusError: null | Error;
  audioInputTestReport: null | AudioInputTest.Report;
  audioOutputTestReport: null | AudioOutputTest.Report;
  networkTestResult: TestResults | null;
  downButtonDisabled: boolean;
  preflightTestInProgress: boolean;
  preflightTestFinished: boolean;
  appIsExpired: boolean;
  isSupported: boolean;
}

export interface finalResultsMinifiedType {
  date: string;
  email: string | undefined;
  browserInfo: {
    name: string | undefined;
    version: string | undefined;
  };
  operatingSystem: {
    name: string | undefined;
    version: string | undefined;
  };
  inputDevice: string | undefined;
  outputDevice: string | undefined;
  connectivity: {
    signalingRegion: string;
  };
  twilioServices: TwilioStatus | null;
  conferencingServices: ConferencingStatus | null;
  preflightTestReport: {
    callQuality: string;
    edge: string;
    jitter: string;
    latency: string;
    packetLoss: string;
  };
}

export type ACTIONTYPE =
  | { type: 'set-active-pane'; newActivePane: ActivePane }
  | { type: 'next-pane' }
  | { type: 'previous-pane' }
  | { type: 'set-user-info'; userInfo: UserInfo }
  | { type: 'set-devices'; devices: MediaDeviceInfo[] }
  | { type: 'set-device-error'; error: Error }
  | { type: 'set-audio-input-test-report'; report: AudioInputTest.Report }
  | { type: 'set-audio-output-test-report'; report: AudioOutputTest.Report }
  | { type: 'set-network-test-result'; result: TestResults }
  | { type: 'preflight-progress'; progress: string }
  | { type: 'preflight-completed'; report: PreflightTestReport }
  | { type: 'preflight-failed'; error: Error }
  | { type: 'preflight-token-failed'; error: Error }
  | { type: 'set-twilio-status'; statusObj: TwilioStatus }
  | { type: 'set-twilio-status-error'; error: Error }
  | { type: 'set-conferencing-status'; statusObj: ConferencingStatus }
  | { type: 'set-conferencing-status-error'; error: Error }
  | { type: 'preflight-started' }
  | { type: 'preflight-finished' }
  | { type: 'set-app-is-expired' };

type AppStateContextType = {
  state: stateType;
  dispatch: React.Dispatch<ACTIONTYPE>;
  nextPane: () => void;
  userAgentInfo: UAParser.IResult;
  downloadFinalTestResults: () => void;
  finalResultsMinified: () => finalResultsMinifiedType;
};

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    getHighEntropyValues: (hints: string[]) => Promise<{
      fullVersionList?: { brand: string; version: string }[];
    }>;
  };
};

export const initialState = {
  activePane: ActivePane.GetStarted,
  userInfo: null,
  audioGranted: false,
  deviceError: null,
  preflightTest: {
    progress: null,
    report: null,
    error: null,
    tokenError: null,
  },
  twilioStatus: null,
  twilioStatusError: null,
  conferencingStatus: null,
  conferencingStatusError: null,
  audioInputTestReport: null,
  audioOutputTestReport: null,
  networkTestResult: null,
  downButtonDisabled: false,
  preflightTestInProgress: false,
  preflightTestFinished: false,
  appIsExpired: false,
  isSupported: Device.isSupported,
};

export const AppStateContext = createContext<AppStateContextType>(null!);

export function useAppStateContext() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppStateContext must be used within an AppStateProvider');
  }
  return context;
}

// helper function for determining whether to disable the down arrow button:
export const isDownButtonDisabled = (currentState: stateType) => {
  const {
    activePane,
    preflightTestFinished,
    networkTestResult,
    audioInputTestReport,
    audioOutputTestReport,
    appIsExpired,
  } = currentState;

  const testResults = networkTestResult;
  const isNetworkTestRunningButNotCompleted = activePane === ActivePane.NetworkTest && !preflightTestFinished;
  const connectionFailed = activePane === ActivePane.Connectivity && !!testResults?.errors.preflight;
  const deviceTestErrors = !!audioInputTestReport?.errors.length || !!audioOutputTestReport?.errors.length;
  const onDeviceCheck = activePane === ActivePane.DeviceCheck || activePane === ActivePane.DeviceError;
  const unsupportedBrowser = activePane === ActivePane.BrowserTest && !currentState.isSupported;
  const UserInfo = activePane === ActivePane.UserInfo;

  return (
    appIsExpired ||
    isNetworkTestRunningButNotCompleted ||
    connectionFailed ||
    !ActivePane[activePane + 1] ||
    deviceTestErrors ||
    onDeviceCheck ||
    UserInfo ||
    unsupportedBrowser
  );
};

/**
 * This reducer function helps with the logic used to decide the activePane,
 * as well as which panes are to be shown to the user, and when.
 */

export const appStateReducer = produce((draft: stateType, action: ACTIONTYPE) => {
  switch (action.type) {
    case 'set-active-pane':
      draft.activePane = action.newActivePane;
      break;

    case 'next-pane':
      switch (draft.activePane) {
        case ActivePane.UserInfo:
          if (draft.audioGranted) {
            draft.activePane = ActivePane.AudioTest;
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        case ActivePane.DeviceCheck:
          if (draft.deviceError) {
            draft.activePane = ActivePane.DeviceError;
          } else {
            draft.activePane = ActivePane.AudioTest;
          }
          break;
        default:
          draft.activePane++;
          break;
      }
      break;

    case 'previous-pane':
      switch (draft.activePane) {
        case ActivePane.AudioTest:
          if (draft.audioGranted) {
            draft.activePane = ActivePane.UserInfo;
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        default:
          draft.activePane--;
          break;
      }
      break;
    case 'set-user-info':
      draft.userInfo = action.userInfo;
      break;

    case 'set-devices':
      draft.audioGranted = action.devices.filter((d) => d.kind === 'audioinput').every((d) => d.label);
      break;

    case 'set-device-error':
      draft.deviceError = action.error;
      draft.activePane = ActivePane.DeviceError;
      break;

    case 'preflight-progress':
      draft.preflightTest.progress = action.progress;
      break;

    case 'preflight-completed':
      draft.preflightTest.report = action.report;
      draft.preflightTest.progress = null;
      break;

    case 'preflight-failed':
      draft.preflightTest.error = action.error;
      draft.preflightTest.progress = null;
      break;

    case 'preflight-token-failed':
      draft.preflightTest.tokenError = action.error;
      break;

    case 'set-twilio-status':
      draft.twilioStatus = action.statusObj;
      break;

    case 'set-twilio-status-error':
      draft.twilioStatusError = action.error;
      break;

    case 'set-conferencing-status':
      draft.conferencingStatus = action.statusObj;
      break;

    case 'set-conferencing-status-error':
      draft.conferencingStatusError = action.error;
      break;

    case 'set-audio-input-test-report':
      draft.audioInputTestReport = action.report;
      break;

    case 'set-audio-output-test-report':
      draft.audioOutputTestReport = action.report;
      break;

    case 'set-network-test-result':
      draft.networkTestResult = action.result;
      break;

    case 'preflight-started':
      draft.preflightTestInProgress = true;
      draft.preflightTestFinished = false;
      break;

    case 'preflight-finished':
      draft.preflightTestFinished = true;
      draft.preflightTestInProgress = false;
      break;

    case 'set-app-is-expired':
      draft.appIsExpired = true;
  }

  const currentState = current(draft);

  draft.downButtonDisabled = isDownButtonDisabled(currentState);
});

export const AppStateProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);
  const { getTwilioStatus } = useTwilioStatus(dispatch);
  const { getConferencingStatus } = useConferencingStatus(dispatch);
  const [userAgentInfo, setUserAgentInfo] = useState<UAParser.IResult>(() => new UAParser().getResult());

  const downloadFinalTestResults = () => {
    const getSignalingGatewayStatus = (testResult: TestResults | null) => {
      if (testResult?.results.preflight || testResult?.errors.preflight?.hasConnected) {
        return 'Reachable';
      }
      return 'Unreachable';
    };
    const signalingGateway = getSignalingGatewayStatus(state.networkTestResult);

    const finalTestResults = {
      userIdentity: state.userInfo,
      audioTestResults: { inputTest: state.audioInputTestReport, outputTest: state.audioOutputTestReport },
      browserInformation: userAgentInfo,
      connectivityResults: {
        signalingRegion: signalingGateway,
      },
      conferencingStatus: state.conferencingStatus,
      callTestReport: state.networkTestResult,
    };

    const link = document.createElement('a');
    link.download = `test_results${getFormattedDate()}.json`;
    link.href = URL.createObjectURL(
      new Blob([JSON.stringify(finalTestResults, null, 2)], {
        type: 'application/json',
      })
    );
    link.click();
  };

  const finalResultsMinified = (): finalResultsMinifiedType => {
    const {
      jitter: jitterQualityScore,
      latency: latencyQualityScore,
      packetLoss: packetLossQualityScore,
    } = getQualityScore(state.networkTestResult?.results.preflight as PreflightTestReport);
    const { signalingGateway } = getConnectivityStatus(state.networkTestResult, state.conferencingStatusError);
    const getJitter = (jitter: PreflightTest.Stats | undefined) => {
      if (jitter) {
        const { min, average, max } = jitter;
        return `${QualityScore[jitterQualityScore.qualityScore]} (${round(min)} / ${round(average)} / ${round(
          max
        )}, min/avg/max)`;
      }
      return 'Not available';
    };
    const getLatency = (latency: PreflightTest.Stats | undefined) => {
      if (latency) {
        const { min, average, max } = latency;
        return `${QualityScore[latencyQualityScore.qualityScore]} (${round(min, 0)} / ${round(average, 0)} / ${round(
          max,
          0
        )} ms, min/avg/max)`;
      }
      return 'Not available';
    };

    return {
      date: createUTCTimestamp(),
      email: state.userInfo?.email,
      browserInfo: { name: userAgentInfo.browser.name, version: userAgentInfo.browser.version },
      operatingSystem: { name: userAgentInfo.os.name, version: userAgentInfo.os.version },
      inputDevice:
        typeof state.audioInputTestReport?.deviceId === 'string'
          ? state.audioInputTestReport.deviceId
          : 'Not available',
      outputDevice:
        typeof state.audioOutputTestReport?.deviceId === 'string'
          ? state.audioOutputTestReport.deviceId
          : 'Not available',
      connectivity: {
        signalingRegion: signalingGateway,
      },
      twilioServices: state.twilioStatus ?? null,
      conferencingServices: state.conferencingStatus ?? null,
      preflightTestReport: {
        callQuality: state.networkTestResult?.results.preflight?.callQuality
          ? capitaliseFirstLetter(state.networkTestResult.results.preflight.callQuality)
          : 'Not available',
        edge: `${
          edgeNameMap[state.networkTestResult?.results.preflight?.edge as keyof typeof edgeNameMap] ?? 'Not available'
        }`,
        jitter: getJitter(state.networkTestResult?.results.preflight?.stats?.jitter),
        latency: getLatency(state.networkTestResult?.results.preflight?.stats?.rtt),
        packetLoss:
          typeof state.networkTestResult?.results.preflight?.totals?.packetsLostFraction === 'number'
            ? `${QualityScore[packetLossQualityScore.qualityScore]} (${packetLossQualityScore.average}%)`
            : 'Not available',
      },
    };
  };

  const nextPane = useCallback(() => {
    switch (state.activePane) {
      case ActivePane.GetStarted:
        getTwilioStatus();
        getConferencingStatus();
        dispatch({ type: 'next-pane' });
        break;
      default:
        dispatch({ type: 'next-pane' });
    }
  }, [getTwilioStatus, getConferencingStatus, state, dispatch]);

  useEffect(() => {
    const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData;

    if (!userAgentData) {
      return;
    }

    userAgentData
      .getHighEntropyValues(['fullVersionList'])
      .then(({ fullVersionList }) => {
        const browserVersion = fullVersionList?.find(
          ({ brand }) => brand !== 'Chromium' && !brand.startsWith('Not')
        )?.version;

        if (browserVersion) {
          setUserAgentInfo((currentUserAgentInfo) => ({
            ...currentUserAgentInfo,
            browser: { ...currentUserAgentInfo.browser, version: browserVersion },
          }));
        }
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => dispatch({ type: 'set-devices', devices }));

    axios.post(TOKEN_API_URL).catch((error: unknown) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error?.message === 'token server expired') {
          dispatch({ type: 'set-app-is-expired' });
        }
      } else {
        console.error('An unexpected error occurred:', error);
      }
    });
  }, []);

  return (
    <AppStateContext.Provider
      value={{ state, dispatch, nextPane, userAgentInfo, downloadFinalTestResults, finalResultsMinified }}
    >
      {children}
    </AppStateContext.Provider>
  );
};
